import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { formatISO, format } from "date-fns";
import {
  MDXEditor,
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  markdownShortcutPlugin,
} from "@mdxeditor/editor";
import "@mdxeditor/editor/style.css";
import { useAuth0 } from "@auth0/auth0-react";
import {
  DocumentTextIcon,
  NewspaperIcon,
  BookOpenIcon,
  ChatBubbleBottomCenterTextIcon,
  ArchiveBoxIcon,
  PencilIcon,
  SpeakerWaveIcon,
  ClipboardDocumentIcon,
  InformationCircleIcon,
  ListBulletIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";

import ErrorNotification from "../components/ErrorNotification";
import ProjectLoader from "../components/ProjectLoader";
import SEO from "../components/SEO";
import { useToast } from "../components/Toast";
import SelectNotesDialog from "../components/SelectNotesDialog";
import { Project, ProjectNote, GetNotesResponse } from "../types";
import ProjectCommandSidebar from "../components/ProjectCommandSidebar";
import ProjectDetailsReviewChanges from "./ProjectDetailsReviewChanges";
import SpinningCircle from "../components/SpinningCircle";
import {
  processMarkdownContentForEditor,
  reverseProcessMarkdownContent,
  useDebounce,
} from "../utils";

const PROJECT_TYPE_ICONS: Record<string, React.ElementType> = {
  "blog-post": ListBulletIcon,
  article: NewspaperIcon,
  "book-outline": BookOpenIcon,
  "social-post": ChatBubbleBottomCenterTextIcon,
};

enum AudienceType {
  TECHNICAL = "technical",
  BUSINESS = "business",
  GENERAL = "general",
  OTHER = "other",
}

const getIsStandardAudience = (audience: string): audience is AudienceType =>
  Object.values(AudienceType).some((val) => val === audience);

interface AudienceField {
  id: AudienceType;
  label: string;
  description: string;
}

const AUDIENCE_LABELS: Record<string, string> = {
  technical: "Technical professionals",
  business: "Business professionals",
  general: "General audience",
};

const SIDEBAR_EXPANDED_WIDTH = "w-[25%]";
const SIDEBAR_COLLAPSED_WIDTH = "w-12";

const ProjectDetails = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { getAccessTokenSilently } = useAuth0();
  const [project, setProject] = useState<Project | null>(null);
  const [originalProcessedOutput, setOriginalProcessedOutput] = useState("");
  const [projectType, setProjectType] = useState<string>("blog-post");
  const [isLoadingProject, setIsLoadingProject] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [isArchiving, setIsArchiving] = useState(false);
  const [showArchiveConfirmation, setShowArchiveConfirmation] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);
  const [showReactivateConfirmation, setShowReactivateConfirmation] =
    useState(false);
  const [activeTab, setActiveTab] = useState(projectType);
  const [rightColumnActiveTab, setRightColumnActiveTab] = useState<
    "details" | "notes"
  >("details");
  const [notes, setNotes] = useState<ProjectNote[]>([]);
  const [showEditNotesDialog, setShowEditNotesDialog] = useState(false);
  const [editingNotes, setEditingNotes] = useState<GetNotesResponse | null>(
    null
  );
  const [editingCurrentPage, setEditingCurrentPage] = useState(0);
  const [isLoadingEditingNotes, setIsLoadingEditingNotes] = useState(false);
  const [selectedNoteIds, setSelectedNoteIds] = useState<number[]>([]);
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [showEditDetailsDialog, setShowEditDetailsDialog] = useState(false);
  const [editedIdea, setEditedIdea] = useState("");
  const [editedAudience, setEditedAudience] = useState<AudienceType>(
    AudienceType.GENERAL
  );
  const [editedOtherAudience, setEditedOtherAudience] = useState("");
  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const [copied, setCopied] = useState(false);
  const [expandedEditingNoteIds, setExpandedEditingNoteIds] = useState<
    number[]
  >([]);
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [note, setNote] = useState<ProjectNote | null>(null);
  const [noteId, setNoteId] = useState<number | null>(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [localContent, setLocalContent] = useState("");
  const [isSavingContent, setIsSavingContent] = useState(false);
  const [isHandlingCommand, setIsHandlingCommand] = useState(false);
  const [isRightColumnCollapsed, setIsRightColumnCollapsed] = useState(false);
  const [isLeftColumnCollapsed, setIsLeftColumnCollapsed] = useState(false);

  const fetchEditingNotes = useCallback(
    async (pageNumber: number = 0) => {
      setIsLoadingEditingNotes(true);
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/v2/notes?pageNumber=${pageNumber}&pageSize=20`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch notes");
        }

        const data = await response.json();

        if (pageNumber > 0 && editingNotes) {
          setEditingNotes({
            notes: [...editingNotes.notes, ...data.notes],
            pagination: data.pagination,
          });
        } else {
          setEditingNotes(data);
        }
      } catch (error) {
        console.error("Error fetching notes:", error);
        showToast("Failed to load notes", "red");
      } finally {
        setIsLoadingEditingNotes(false);
      }
    },
    [getAccessTokenSilently, editingNotes, showToast]
  );

  const handleCopy = useCallback(async () => {
    if (!project) return;
    try {
      window.analytics?.track("Project Details | Copied Project", {
        projectId: projectId,
      });

      await navigator.clipboard.writeText(project.output);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text:", err);
      showToast("Failed to copy project output", "red");
    }
  }, [project, projectId, showToast]);

  useEffect(() => {
    const handleKeyDownCopy = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "c") {
        if (!window.getSelection()?.toString()) {
          event.preventDefault();
          handleCopy();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDownCopy);
    return () => document.removeEventListener("keydown", handleKeyDownCopy);
  }, [handleCopy]);

  useEffect(() => {
    const fetchProjectDetails = async () => {
      if (!projectId) return;

      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/v2/projects/${projectId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch project details");
        }

        const data = await response.json();
        let updatedProject: Project = data.project;
        const processedOutput = processMarkdownContentForEditor(
          updatedProject.output
        );
        setOriginalProcessedOutput(processedOutput);
        setLocalContent(processedOutput);
        setProject(updatedProject);
        setProjectType(data.project.projectType);
        // Filter out archived notes
        setNotes(
          data.notes.filter((note: ProjectNote) => note.status !== "archived")
        );
      } catch (error) {
        console.error("Error fetching project details:", error);
        setError("Failed to fetch project details. Please try again.");
      } finally {
        setIsLoadingProject(false);
      }
    };

    fetchProjectDetails();
  }, [projectId, getAccessTokenSilently]);

  useEffect(() => {
    setActiveTab(projectType);
  }, [projectType]);

  /*  This handles the edge case where the user is on the details tab and the window is resized to desktop size
      The details tab is already visible on the left column in desktop view so we don't want to show it on the 
      middle column, instead we resort back to showing the projectType tab.
  */
  useEffect(() => {
    const handleResize = () => {
      const lgBreakpoint = 1024;
      const isDesktop = window.innerWidth >= lgBreakpoint;

      if (isDesktop && activeTab !== projectType) {
        setActiveTab(projectType);
      }
    };

    window.addEventListener("resize", handleResize);

    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, [activeTab, projectType]);

  const handleArchive = async () => {
    if (!projectId || isArchiving) return;

    setIsArchiving(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/v2/projects/${projectId}/archive`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            currentTimestamp: formatISO(new Date()),
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to archive project");
      }

      navigate("/projects");
      showToast("Project archived successfully", "green");
    } catch (error) {
      console.error("Error archiving project:", error);
      showToast("Failed to archive project", "red");
    } finally {
      setIsArchiving(false);
    }
  };

  const handleReactivate = async () => {
    if (!projectId || isReactivating) return;

    // Track user intent to reactivate project
    window.analytics?.track("Projects | Reactivated Project", {
      projectId: projectId,
      projectType: project?.projectType,
    });

    setIsReactivating(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/v2/projects/${projectId}/reactivate`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            currentTimestamp: formatISO(new Date()),
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to reactivate project");
      }

      // Update the project status locally
      if (project) {
        setProject({ ...project, status: "IN_PROGRESS" });
      }

      showToast("Project reactivated successfully", "green");
    } catch (error) {
      console.error("Error reactivating project:", error);
      showToast("Failed to reactivate project", "red");
    } finally {
      setIsReactivating(false);
      setShowReactivateConfirmation(false);
    }
  };

  const debouncedContent = useDebounce(localContent, 2000); // debounce content changes for 2 seconds

  const handleContentChange = useCallback((markdown: string) => {
    setLocalContent(markdown);
  }, []);

  const handleSaveContent = useCallback(
    async (processedMarkdown: string) => {
      if (
        !project ||
        !projectId ||
        processedMarkdown === originalProcessedOutput
      )
        return;
      const markdown = reverseProcessMarkdownContent(processedMarkdown);
      if (markdown === project.output) return;
      setIsSavingContent(true);
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/v2/projects/${projectId}/output`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              output: markdown,
              currentTimestamp: formatISO(new Date()),
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to update project content");
        }

        // Update local state
        setProject({ ...project, output: markdown });
        setOriginalProcessedOutput(processedMarkdown);
        showToast("Content updated successfully", "green");
      } catch (error) {
        console.error("Error updating content:", error);
        showToast("Failed to update content", "red");
      } finally {
        setIsSavingContent(false);
      }
    },
    [
      project,
      projectId,
      originalProcessedOutput,
      getAccessTokenSilently,
      showToast,
    ]
  );

  useEffect(() => {
    if (debouncedContent && originalProcessedOutput !== debouncedContent) {
      handleSaveContent(debouncedContent);
    }
  }, [debouncedContent, originalProcessedOutput, handleSaveContent]);

  const handleSaveNotes = async () => {
    if (!projectId) return;

    setIsSavingNotes(true);
    setIsRegenerating(true);
    try {
      const token = await getAccessTokenSilently();

      // Convert to array of objects with id property
      const noteIds = selectedNoteIds.map((id) => ({ id: Number(id) }));

      const requestBody = {
        currentTimestamp: formatISO(new Date()),
        notes: noteIds,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/v2/projects/${projectId}/notes`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const errorMessage =
          errorData?.errorDescription ||
          errorData?.message ||
          "Failed to update project notes";
        throw new Error(errorMessage);
      }

      // Close dialog and switch to blog post outline tab immediately
      setShowEditNotesDialog(false);
      setActiveTab(projectType);

      // Fetch updated project details
      const projectResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/v2/projects/${projectId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!projectResponse.ok) {
        throw new Error("Failed to fetch updated project details");
      }

      const projectData = await projectResponse.json();
      setProject(projectData.project);
      setNotes(projectData.notes || []);
      showToast("Project notes updated successfully", "green");
    } catch (error) {
      console.error("Error updating project notes:", error);
      showToast(
        error instanceof Error
          ? error.message
          : "Failed to update project notes",
        "red"
      );
    } finally {
      setIsSavingNotes(false);
      setIsRegenerating(false);
    }
  };

  const handleSaveDetails = async () => {
    if (!projectId) return;

    setIsSavingDetails(true);
    setIsRegenerating(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/v2/projects/${projectId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            idea: editedIdea.trim(),
            targetAudience:
              editedAudience === "other"
                ? editedOtherAudience.trim()
                : editedAudience,
            currentTimestamp: formatISO(new Date()),
          }),
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          showToast("Project not found. It may have been deleted.", "red");
          navigate("/projects");
          return;
        }
        const errorData = await response.json().catch(() => null);
        const errorMessage =
          errorData?.errorDescription ||
          errorData?.message ||
          "Failed to update project details";
        throw new Error(errorMessage);
      }

      // Close dialog immediately to show loading state
      setShowEditDetailsDialog(false);

      // Fetch updated project details to get the regenerated output
      const projectResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/v2/projects/${projectId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!projectResponse.ok) {
        throw new Error("Failed to fetch updated project details");
      }

      const projectData = await projectResponse.json();
      setProject(projectData.project);
      setNotes(projectData.notes || []);
      showToast("Project details updated successfully", "green");
    } catch (error) {
      console.error("Error updating project details:", error);
      showToast(
        error instanceof Error
          ? error.message
          : "Failed to update project details",
        "red"
      );
    } finally {
      setIsSavingDetails(false);
      setIsRegenerating(false);
    }
  };

  const toggleNoteSelection = (noteId: number) => {
    setSelectedNoteIds((prev) =>
      prev.includes(noteId)
        ? prev.filter((id) => id !== noteId)
        : [...prev, noteId]
    );
  };

  const handleCancelEdit = useCallback(() => {
    setIsEditingDetails(false);
    setNote(null);
    setNoteId(null);
  }, []);

  useEffect(() => {
    const handleKeyDownEdit = (event: KeyboardEvent) => {
      if (
        (event.metaKey || event.ctrlKey) &&
        event.key === "e" &&
        !isEditingDetails &&
        !note?.isEchoTextEnabled
      ) {
        event.preventDefault();
        window.analytics?.track("Note Details | Entered Edit Mode", {
          noteId: noteId,
          method: "KeyboardShortcut",
        });
        setIsEditingDetails(true);
      } else if (event.key === "Escape" && isEditingDetails) {
        event.preventDefault();
        handleCancelEdit();
      }
    };

    document.addEventListener("keydown", handleKeyDownEdit);
    return () => document.removeEventListener("keydown", handleKeyDownEdit);
  }, [isEditingDetails, note, noteId, handleCancelEdit]);

  // NOTE: Not the most elegant solution since fallback is hardcoded and just happens to
  // match the only project type we currently allow, but it works for now
  const tabs = useMemo(
    () => [
      {
        name: "Blog post outline",
        value: projectType,
        icon:
          PROJECT_TYPE_ICONS[projectType] || PROJECT_TYPE_ICONS["blog-post"],
        current: activeTab === projectType,
        showOnMobile: true,
      },
      {
        name: "Prompt Echo",
        value: "prompt",
        icon: ChatBubbleBottomCenterTextIcon,
        current: activeTab === "prompt",
        showOnMobile: true,
        hideOnDesktop: true,
      },
      {
        name: "Related notes",
        value: "context",
        icon: DocumentTextIcon,
        current: activeTab === "context",
        showOnMobile: true,
      },
      {
        name: "Project details",
        value: "details",
        icon: InformationCircleIcon,
        current: activeTab === "details",
        showOnMobile: true,
        hideOnDesktop: true,
      },
    ],
    [activeTab, projectType]
  );

  if (error) {
    return <ErrorNotification message={error} />;
  }

  if (!project && !isLoadingProject) {
    return <ErrorNotification message="Project not found." />;
  }

  const classNames = (...classes: string[]) => {
    return classes.filter(Boolean).join(" ");
  };

  const handleSaveChanges = (finalOutput: string) => {
    if (!project) return;
    // TODO: check this
    setProject({ ...project, output: finalOutput, stagedOutput: null });
  };

  const handlePromptResponse = (output: string) => {
    if (!project || !output) return;
    setProject({ ...project, stagedOutput: output });
  };

  const handleCommandSidebarLoadingStateChange = (isLoading: boolean) => {
    setIsHandlingCommand(isLoading);
  };

  const projectDetailsCard = project && (
    <div className="flex flex-col items-start space-y-4">
      <button
        onClick={() => {
          setEditedIdea(project.idea);
          if (getIsStandardAudience(project.targetAudience)) {
            setEditedAudience(project.targetAudience);
          } else {
            setEditedAudience(AudienceType.OTHER);
          }
          setEditedOtherAudience(project.targetAudience);
          setShowEditDetailsDialog(true);
        }}
        disabled={isLoadingProject}
        title={
          isLoadingProject
            ? "Please wait while the outline is being generated"
            : "Edit project details"
        }
        className="inline-flex items-center gap-1 rounded bg-white px-2 py-1 text-xs font-semibold text-gray-600 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <PencilIcon className="h-4 w-4" />
        <span>Edit Details</span>
      </button>

      <div className="w-full">
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-500">
              Basic Idea
            </label>
            <p className="mt-1 text-sm text-gray-900">{project.idea}</p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-500">
              Target Audience
            </label>
            <p className="mt-1 text-sm text-gray-900">
              {AUDIENCE_LABELS[project.targetAudience] ||
                project.targetAudience}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const archiveReactivateButton =
    project &&
    (project.status === "archived" ? (
      <button
        onClick={() => setShowReactivateConfirmation(true)}
        disabled={isReactivating || isLoadingProject}
        title={
          isLoadingProject
            ? "Please wait while the outline is being generated"
            : isReactivating
            ? "Reactivating..."
            : "Reactivate this project"
        }
        className="inline-flex gap-1 rounded bg-white px-2 py-1 text-xs font-semibold text-green-600 shadow-sm ring-1 ring-inset ring-green-300 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ArchiveBoxIcon className="h-4 w-4" />
        <span>{isReactivating ? "Reactivating..." : "Reactivate"}</span>
      </button>
    ) : (
      <button
        onClick={() => setShowArchiveConfirmation(true)}
        disabled={isArchiving || isLoadingProject}
        title={
          isLoadingProject
            ? "Please wait while the outline is being generated"
            : isArchiving
            ? "Archiving..."
            : "Archive this project"
        }
        className="inline-flex gap-1 rounded bg-white px-2 py-1 text-xs font-semibold text-red-600 shadow-sm ring-1 ring-inset ring-red-300 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ArchiveBoxIcon className="h-4 w-4" />
        <span>{isArchiving ? "Archiving..." : "Archive"}</span>
      </button>
    ));

  return (
    <>
      <SEO
        title={`Project Details | Echo`}
        description="View and manage your project details"
        isAuthRequired={true}
        canonicalPath={`/projects/${projectId}`}
      />
      <div className="flex flex-col flex-1 justify-between">
        <div className="flex flex-row relative">
          {/* Project Command Sidebar - LEFT COLUMN */}
          <div
            className={`sticky top-[73px] hidden lg:block ${
              isLeftColumnCollapsed
                ? SIDEBAR_COLLAPSED_WIDTH
                : SIDEBAR_EXPANDED_WIDTH
            } h-[calc(100vh-73px)] transition-all duration-300 bg-gray-100 border-r border-gray-200`}
          >
            <div
              className={`flex ${
                isLeftColumnCollapsed ? "justify-end" : "justify-between"
              } items-center border-b border-gray-200 w-full relative`}
            >
              {!isLeftColumnCollapsed && (
                <div className="flex items-center p-4">
                  <h3 className="text-sm font-medium text-gray-900">
                    Echo Assistant
                  </h3>
                </div>
              )}
              <button
                onClick={() => setIsLeftColumnCollapsed(!isLeftColumnCollapsed)}
                className={`px-2 py-4 ${
                  !isLeftColumnCollapsed ? "border-l border-gray-200" : ""
                } hover:bg-gray-100`}
                title={isLeftColumnCollapsed ? "Expand" : "Collapse"}
              >
                {isLeftColumnCollapsed ? (
                  <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronLeftIcon className="h-5 w-5 text-gray-500" />
                )}
              </button>
            </div>
            {!isLeftColumnCollapsed && projectId && (
              <ProjectCommandSidebar
                projectId={projectId}
                onPromptResponse={handlePromptResponse}
                onLoadingStateChange={handleCommandSidebarLoadingStateChange}
              />
            )}
          </div>

          {/* Middle Column */}
          <div className="flex flex-1 flex-col w-full lg:bg-white">
            {/* Mobile view tabs */}
            <div className="block lg:hidden px-3 py-3 w-full border-b border-gray-200">
              <select
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value)}
                aria-label="Select a tab"
                className="w-full appearance-none rounded-md bg-white py-2 pl-3 pr-8 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-blue-500"
              >
                {tabs
                  .filter((tab) => tab.showOnMobile)
                  .map((tab) => (
                    <option key={tab.value} value={tab.value}>
                      {tab.name}
                    </option>
                  ))}
              </select>
            </div>

            {activeTab === projectType && (
              <div className="relative bg-white py-16 px-8 lg:max-w-[740px] mx-auto">
                {(isSavingContent || isHandlingCommand) && (
                  <div className="absolute top-5 left-10 inline-flex justify-center items-center">
                    <SpinningCircle size="sm" />
                  </div>
                )}
                <button
                  onClick={handleCopy}
                  disabled={isLoadingProject || isRegenerating}
                  title={
                    isLoadingProject || isRegenerating
                      ? "Please wait while the outline is being generated"
                      : "Copy outline to clipboard"
                  }
                  className="absolute top-6 right-8 inline-flex items-center gap-1 rounded bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-0 disabled:cursor-not-allowed"
                >
                  <ClipboardDocumentIcon className="h-4 w-4" />
                  <span>{copied ? "Copied!" : "Copy"}</span>
                  <span className="text-gray-400 ml-1">
                    {navigator.platform.toLowerCase().includes("mac")
                      ? "⌘"
                      : "ctrl"}
                    C
                  </span>
                </button>

                {isLoadingProject || isRegenerating ? (
                  <ProjectLoader />
                ) : project ? (
                  project.stagedOutput ? (
                    <ProjectDetailsReviewChanges
                      projectId={project.id}
                      output={project.output}
                      stagedOutput={project.stagedOutput}
                      onSaveChanges={handleSaveChanges}
                    />
                  ) : (
                    <div className="prose prose-sm max-w-none prose-ul:mt-1 prose-li:mt-0.5 prose-headings:mb-2">
                      <MDXEditor
                        markdown={localContent}
                        onChange={handleContentChange}
                        contentEditableClassName="prose prose-sm max-w-none prose-ul:mt-1 prose-li:mt-0.5 prose-headings:mb-2"
                        plugins={[
                          headingsPlugin(),
                          listsPlugin(),
                          quotePlugin(),
                          markdownShortcutPlugin(),
                        ]}
                        onBlur={() => handleSaveContent(localContent)}
                      />
                    </div>
                  )
                ) : null}
              </div>
            )}

            {activeTab === "context" && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={() => {
                      setSelectedNoteIds(notes.map((note) => note.id));
                      setShowEditNotesDialog(true);
                      setEditingCurrentPage(0);
                      fetchEditingNotes(0);
                    }}
                    disabled={isLoadingProject}
                    title={
                      isLoadingProject
                        ? "Please wait while the outline is being generated"
                        : "Edit related notes"
                    }
                    className="inline-flex items-center gap-1 rounded bg-white px-2 py-1 text-xs font-semibold text-gray-600 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <PencilIcon className="h-4 w-4" />
                    <span>Edit Notes</span>
                  </button>
                </div>
                {notes.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    You don't have any related notes yet.
                  </p>
                ) : (
                  <div className="bg-white shadow rounded-lg">
                    <ul className="divide-y divide-gray-200">
                      {notes.map((note) => (
                        <li key={note.id}>
                          <Link
                            to={`/notes/${note.id}`}
                            className="block hover:bg-gray-50"
                          >
                            <div className="px-4 py-4 sm:px-6">
                              <div className="flex flex-col items-start justify-between">
                                <div className="flex items-center gap-1 pb-1">
                                  {note.format === "audio" && (
                                    <SpeakerWaveIcon className="h-3 w-3 text-gray-600" />
                                  )}
                                  {note.author === "system" && (
                                    <img
                                      className="h-3 w-auto opacity-50"
                                      src="/logo192Black.png"
                                      alt="System Logo"
                                    />
                                  )}
                                  <p className="text-xs text-gray-500">
                                    {format(
                                      new Date(note.createdAt),
                                      "MMM dd, yyyy"
                                    )}
                                  </p>
                                </div>
                                <p className="text-sm font-medium text-gray-900">
                                  {note.title}
                                </p>
                              </div>
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {activeTab === "details" && project && (
              <div className="p-6 flex flex-col gap-6 items-start">
                {projectDetailsCard}
                {archiveReactivateButton}
              </div>
            )}

            {activeTab === "prompt" && project && (
              <div className="block w-full h-full">
                {projectId && (
                  <ProjectCommandSidebar
                    projectId={projectId}
                    onPromptResponse={handlePromptResponse}
                    onLoadingStateChange={
                      handleCommandSidebarLoadingStateChange
                    }
                  />
                )}
              </div>
            )}

            {/* Edit Notes Dialog */}
            <SelectNotesDialog
              show={showEditNotesDialog}
              onClose={() => setShowEditNotesDialog(false)}
              onSave={handleSaveNotes}
              isLoadingEditingNotes={isLoadingEditingNotes}
              editingNotes={editingNotes}
              selectedNoteIds={selectedNoteIds}
              expandedEditingNoteIds={expandedEditingNoteIds}
              editingCurrentPage={editingCurrentPage}
              isLoadingMore={isLoadingEditingNotes}
              onToggleNoteSelection={toggleNoteSelection}
              onToggleNoteExpansion={(noteId: number) => {
                setExpandedEditingNoteIds((prev) =>
                  prev.includes(noteId)
                    ? prev.filter((id) => id !== noteId)
                    : [...prev, noteId]
                );
              }}
              onLoadMore={() => {
                const nextPage = editingCurrentPage + 1;
                setEditingCurrentPage(nextPage);
                fetchEditingNotes(nextPage);
              }}
              isSaving={isSavingNotes}
            />
          </div>

          {/* Right Column */}
          <div
            className={`hidden lg:block ${
              isRightColumnCollapsed
                ? SIDEBAR_COLLAPSED_WIDTH
                : SIDEBAR_EXPANDED_WIDTH
            } h-[calc(100vh-73px)] sticky top-[73px] transition-all duration-300 bg-gray-100 border-l border-gray-200 shadow-sm`}
          >
            {project && (
              <div className="">
                {/* Right Column Tabs */}
                <div className="flex border-b max-w-auto border-gray-200 relative">
                  <button
                    onClick={() =>
                      setIsRightColumnCollapsed(!isRightColumnCollapsed)
                    }
                    className={`px-2 py-4 rounded-md ${
                      !isRightColumnCollapsed ? "border-r border-gray-200" : ""
                    } hover:bg-gray-100 mr-2`}
                    title={isRightColumnCollapsed ? "Expand" : "Collapse"}
                  >
                    {isRightColumnCollapsed ? (
                      <ChevronLeftIcon className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                    )}
                  </button>
                  {!isRightColumnCollapsed && (
                    <div className="flex flex-1">
                      <button
                        onClick={() => setRightColumnActiveTab("details")}
                        className={`flex-1 p-4 border-b-2 text-sm font-medium ${
                          rightColumnActiveTab === "details"
                            ? " border-blue-500 text-gray-900"
                            : "text-gray-500 hover:text-gray-700 border-transparent"
                        }`}
                      >
                        Project Details
                      </button>
                      <button
                        onClick={() => setRightColumnActiveTab("notes")}
                        className={`flex-1 p-4 border-b-2 text-sm font-medium ${
                          rightColumnActiveTab === "notes"
                            ? "border-blue-500 text-gray-900"
                            : "text-gray-500 hover:text-gray-700 border-transparent"
                        }`}
                      >
                        Related Notes
                      </button>
                    </div>
                  )}
                </div>

                {/* Project Details Tab */}
                {!isRightColumnCollapsed &&
                  rightColumnActiveTab === "details" && (
                    <div className="p-4 flex flex-col gap-6 items-start">
                      {projectDetailsCard}
                      {archiveReactivateButton}
                    </div>
                  )}

                {/* Related Notes Tab */}
                {!isRightColumnCollapsed &&
                  rightColumnActiveTab === "notes" && (
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-4">
                        <button
                          onClick={() => {
                            setSelectedNoteIds(notes.map((note) => note.id));
                            setShowEditNotesDialog(true);
                            setEditingCurrentPage(0);
                            fetchEditingNotes(0);
                          }}
                          disabled={isLoadingProject}
                          title={
                            isLoadingProject
                              ? "Please wait while the outline is being generated"
                              : "Edit related notes"
                          }
                          className="inline-flex items-center gap-1 rounded bg-white px-2 py-1 text-xs font-semibold text-gray-600 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <PencilIcon className="h-4 w-4" />
                          <span>Edit Notes</span>
                        </button>
                      </div>
                      {notes.length === 0 ? (
                        <p className="text-sm text-gray-500">
                          You don't have any related notes yet.
                        </p>
                      ) : (
                        <div className="bg-white shadow rounded-lg">
                          <ul className="divide-y divide-gray-200">
                            {notes.map((note) => (
                              <li key={note.id}>
                                <Link
                                  to={`/notes/${note.id}`}
                                  className="block hover:bg-gray-50"
                                >
                                  <div className="px-4 py-4 sm:px-6">
                                    <div className="flex flex-col items-start justify-between">
                                      <div className="flex items-center gap-1 pb-1">
                                        {note.format === "audio" && (
                                          <SpeakerWaveIcon className="h-3 w-3 text-gray-600" />
                                        )}
                                        {note.author === "system" && (
                                          <img
                                            className="h-3 w-auto opacity-50"
                                            src="/logo192Black.png"
                                            alt="System Logo"
                                          />
                                        )}
                                        <p className="text-xs text-gray-500">
                                          {format(
                                            new Date(note.createdAt),
                                            "MMM dd, yyyy"
                                          )}
                                        </p>
                                      </div>
                                      <p className="text-sm font-medium text-gray-900">
                                        {note.title}
                                      </p>
                                    </div>
                                  </div>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Details Dialog */}
      <Transition.Root show={showEditDetailsDialog} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          onClose={setShowEditDetailsDialog}
        >
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div>
                  <div className="mt-3 sm:mt-5">
                    <Dialog.Title
                      as="h3"
                      className="text-base font-semibold leading-6 text-gray-900"
                    >
                      Edit Project Details
                    </Dialog.Title>
                    <div className="mt-6 space-y-6">
                      {/* Idea field */}
                      <div>
                        <label
                          htmlFor="idea"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Blog post idea
                        </label>
                        <div className="mt-2">
                          <input
                            type="text"
                            name="idea"
                            id="idea"
                            value={editedIdea}
                            onChange={(e) => setEditedIdea(e.target.value)}
                            className="block w-full rounded-md bg-white px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-blue-500 sm:text-sm/6"
                            placeholder="e.g., The impact of AI on modern software development"
                            required
                          />
                        </div>
                      </div>

                      {/* Target Audience field */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Target Audience
                        </label>
                        <fieldset className="mt-2">
                          <div className="space-y-4">
                            {(
                              [
                                {
                                  id: "technical",
                                  label: "Technical Professionals",
                                  description:
                                    "Software developers, engineers, and technical decision-makers",
                                },
                                {
                                  id: "business",
                                  label: "Business Professionals",
                                  description:
                                    "Managers, executives, and business decision-makers",
                                },
                                {
                                  id: "general",
                                  label: "General Audience",
                                  description:
                                    "Non-technical readers interested in technology and innovation",
                                },
                                {
                                  id: "other",
                                  label: "Other",
                                  description: "Specify your target audience",
                                },
                              ] as AudienceField[]
                            ).map((audience) => (
                              <div
                                key={audience.id}
                                className="relative flex items-start"
                              >
                                <div className="flex h-6 items-center">
                                  <input
                                    id={`audience-${audience.id}`}
                                    name="audience"
                                    type="radio"
                                    checked={editedAudience === audience.id}
                                    onChange={() =>
                                      setEditedAudience(audience.id)
                                    }
                                    className="h-4 w-4 border-gray-300 text-blue-500 focus:ring-blue-500"
                                  />
                                </div>
                                <div className="ml-3">
                                  <label
                                    htmlFor={`audience-${audience.id}`}
                                    className="text-sm font-medium text-gray-900"
                                  >
                                    {audience.label}
                                  </label>
                                  <p className="text-sm text-gray-500">
                                    {audience.description}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </fieldset>

                        {editedAudience === "other" && (
                          <div className="mt-4">
                            <label
                              htmlFor="other-audience"
                              className="block text-sm font-medium text-gray-700"
                            >
                              Specify your target audience
                            </label>
                            <input
                              type="text"
                              name="other-audience"
                              id="other-audience"
                              value={editedOtherAudience}
                              onChange={(e) =>
                                setEditedOtherAudience(e.target.value)
                              }
                              className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6"
                              placeholder="Describe your target audience"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-blue-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 sm:col-start-2 disabled:opacity-50"
                    onClick={handleSaveDetails}
                    disabled={
                      isSavingDetails ||
                      !editedIdea.trim() ||
                      (editedAudience === "other" &&
                        !editedOtherAudience.trim())
                    }
                  >
                    {isSavingDetails ? "Saving..." : "Save"}
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                    onClick={() => setShowEditDetailsDialog(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Archive Confirmation Dialog */}
      {showArchiveConfirmation && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-50">
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ArchiveBoxIcon
                      className="h-6 w-6 text-gray-600"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <h3 className="text-base font-semibold leading-6 text-gray-900">
                      Archive Project
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to archive this project?
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                    onClick={handleArchive}
                  >
                    Archive
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    onClick={() => setShowArchiveConfirmation(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reactivate Confirmation Dialog */}
      {showReactivateConfirmation && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-50">
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ArchiveBoxIcon
                      className="h-6 w-6 text-gray-600"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <h3 className="text-base font-semibold leading-6 text-gray-900">
                      Reactivate Project
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to reactivate this project?
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 sm:ml-3 sm:w-auto"
                    onClick={handleReactivate}
                  >
                    Reactivate
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    onClick={() => setShowReactivateConfirmation(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectDetails;
